import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const TeethWhitening = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Teeth Whitening Fort Worth TX | Professional Whitening | Fort Worth Dental</title>
        <meta name="description" content="Professional teeth whitening in Fort Worth TX. In-office and take-home whitening options. Brighten your smile 6-10 shades whiter. Cosmetic dentistry experts. Call (817) 985-3337." />
        <meta name="keywords" content="teeth whitening fort worth, professional teeth whitening, cosmetic dentistry fort worth tx, smile brightening, tooth whitening near me, in-office whitening" />
        <link rel="canonical" href="https://fortworthdental.com/teeth-whitening" />
        <meta property="og:title" content="Teeth Whitening Fort Worth TX | Professional Whitening" />
        <meta property="og:description" content="Professional teeth whitening by Fort Worth Dental. Brighten your smile today." />
        <meta property="og:url" content="https://fortworthdental.com/teeth-whitening" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-2.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Teeth Whitening in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Brighten Your Smile with Professional Whitening</p>
            <p className="text-sm mt-4 opacity-90">Professional Teeth Whitening | Cosmetic Dentistry | Smile Brightening</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is The Purpose */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/whitening-new-1.jpg" 
                  alt="Professional teeth whitening treatment at Fort Worth Dental Texas" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-2.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Purpose Of Teeth Whitening?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Everyone likes a pretty smile. A pretty smile communicates good health and youth as well as confidence. Conversely, a dull or stained smile is viewed as unhealthy and old. It can be perceived as evidence of poor dental hygiene as well. Teeth whitening provides the opportunity to bring back youthful attractiveness to your smile.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Professional teeth whitening is one of the most popular cosmetic dental procedures in Fort Worth. Our advanced whitening treatments can dramatically brighten your smile, removing years of stains from coffee, tea, wine, and aging.
                </p>
              </div>
            </div>
          </section>

          {/* What Types of Processes */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Types Of Teeth Whitening Processes Are Available?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  There are three main types of teeth whitening processes: Over-the-counter products, tray-based whitening processes performed by the dentist or in the patient's home, and professional whitening processes performed by a dental professional in the dental clinic.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Over-the-counter products can be purchased at a retail store or even online. These options are inexpensive and easy to use. However, they are also less powerful and have less impressive results.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/whitening-new-2.jpg" 
                  alt="Types of professional teeth whitening options Fort Worth TX" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Two Options Available */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              We Have Two Options Available For Our Patients
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Tray-Based Tooth Whitening</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The tray-based tooth whitening process makes use of a tray that fits over the patient's teeth. This tray is filled with a peroxide-based bleaching gel or paste. The tray is worn over the teeth for 1 to 4 hours per day for some patients. Other patients are advised to leave the tray-based system in place overnight.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The benefits of tray-based whitening include convenience (can be done at home), custom fit for your teeth, and professional-grade bleaching gel that provides better results than store-bought options.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Professional In-Office Whitening</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Professional teeth whitening processes that are performed in a dentist's office have the highest level of effectiveness. The dentist is able to use a stronger chemical solution than can be purchased over-the-counter or used in a tray-based process.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The benefits of in-office whitening include immediate, dramatic results (often 6-10 shades whiter in one visit), professional supervision for safety, and the fastest whitening method available.
                </p>
              </div>
            </div>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Teeth Whitening?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced cosmetic dentistry team provides safe, effective teeth whitening with beautiful, natural-looking results.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Professional Results</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Get a Brighter, Whiter Smile Today!</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental for professional teeth whitening in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Related Blog Post - Internal Link for SEO */}
          <section className="mb-16 bg-amber-50 border border-amber-200 p-8 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">📖 Helpful Guide: What to Eat After Whitening</h2>
            <p className="text-gray-700 mb-4">
              Want to make your whitening results last? Learn which foods to eat and avoid in the first 48 hours after treatment.
            </p>
            <a 
              href="/blogs/foods-to-eat-after-teeth-whitening" 
              className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-800"
            >
              Read: What Foods Can You Eat After Teeth Whitening? →
            </a>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Teeth Whitening Consultation in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TeethWhitening;
